// The ONE place that decides whether the app is laid out for a phone.
//
// Result is written to <html data-layout="mobile-portrait|mobile-landscape">
// (absent on desktop), and every mobile style keys off that attribute via
// `:where(html[data-layout^="mobile"]) .Selector` - see style/mobile.css for
// the convention. Desktop never gets the attribute, so no mobile rule can
// ever match there: that's the structural half of "don't touch desktop"
// (the other half is e2e/, which proves it with screenshot diffs).
//
// Two conditions, checked in this order:
// - A PHONE (touch-first: no hover, coarse pointer; and a physical screen
//   whose short side is at most 500px) - portrait or landscape by how the
//   device is actually held. Both come from the SCREEN, never the viewport:
//   on Android the on-screen keyboard shrinks the viewport (see
//   interactive-widget in index.html) - a phone held upright with the
//   keyboard up is 412x420, which a viewport-height test ("short = on its
//   side") read as landscape and flipped the chat into the side-rail
//   layout mid-typing.
// - NARROW: the old breakpoint, unchanged - anything else under 768px wide,
//   e.g. a desktop/Electron window dragged narrow, or a phone whose browser
//   reports a fine pointer.
const COARSE = '(hover: none) and (pointer: coarse)';
const NARROW = '(max-width: 768px)';
const PHONE_MAX_SHORT_SIDE = 500;

const queries = typeof window !== 'undefined' && window.matchMedia
    ? [window.matchMedia(COARSE), window.matchMedia(NARROW)]
    : [];

function screenIsLandscape() {
    const orientation = window.screen && window.screen.orientation;
    if (orientation && orientation.type) return orientation.type.startsWith('landscape');
    // iOS < 16.4 has no screen.orientation - but the legacy angle, and its
    // screen.width/height never swap on rotation, so they can't be used.
    if (typeof window.orientation === 'number') return Math.abs(window.orientation) === 90;
    return window.screen.width > window.screen.height;
}

function isPhoneScreen() {
    return Math.min(window.screen.width, window.screen.height) <= PHONE_MAX_SHORT_SIDE;
}

export function currentLayout() {
    if (!queries.length) return null;
    if (queries[0].matches && isPhoneScreen()) return screenIsLandscape() ? 'mobile-landscape' : 'mobile-portrait';
    if (queries[1].matches) return 'mobile-portrait';
    return null;
}

function apply(layout) {
    const root = document.documentElement;
    if (layout) root.setAttribute('data-layout', layout);
    else root.removeAttribute('data-layout');
}

const listeners = new Set();

// ----- On-screen keyboard (phone layout only) -----
//
// --app-height: the shell's height on phones. 100dvh tracks the browser's
// own toolbars but NOT the on-screen keyboard on iOS - the keyboard slides
// over the page, so a bottom-anchored chat input ends up underneath it.
// visualViewport.height is what's actually visible. (Android Chrome also
// gets `interactive-widget=resizes-content` in index.html, which shrinks the
// layout itself; this stays consistent with that.)
//
// data-typing: set while a text field has focus, so style/mobile.css can
// drop the bottom tab bar out of the way - the keyboard plus a 72px bar
// would otherwise leave a sliver of screen for the conversation.
let appHeight = null;

function syncViewport() {
    const vv = window.visualViewport;
    const root = document.documentElement;
    if (!vv || !currentLayout()) {
        if (appHeight !== null) root.style.removeProperty('--app-height');
        appHeight = null;
        return;
    }
    // Only on an actual change: every write to a custom property on <html>
    // restyles the whole document, and visualViewport fires resize/scroll
    // on every frame while Android's URL bar slides in or out.
    const height = `${Math.round(vv.height)}px`;
    if (height !== appHeight) {
        appHeight = height;
        root.style.setProperty('--app-height', height);
    }
    // iOS also PANS the page to keep a focused input above the keyboard,
    // even with body overflow:hidden. The shell is already sized to the
    // visible area above, so that pan would just push the header off-screen.
    if (window.scrollY !== 0) window.scrollTo(0, 0);
}

// At most once per frame, however many viewport events arrive in it.
let syncQueued = false;
function queueSyncViewport() {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
        syncQueued = false;
        syncViewport();
    });
}

const TEXT_ENTRY = 'input:not([type=checkbox]):not([type=radio]):not([type=range]):not([type=button]):not([type=submit]):not([type=file]), textarea, [contenteditable="true"]';

function onFocusIn(e) {
    if (currentLayout() && e.target.matches && e.target.matches(TEXT_ENTRY)) {
        document.documentElement.setAttribute('data-typing', '');
    }
}

function onFocusOut() {
    // Tabbing from one field straight to another fires focusout before the
    // next focusin - check where focus actually landed before dropping it,
    // or the tab bar would flash back in between the two.
    setTimeout(() => {
        const el = document.activeElement;
        if (!el || !el.matches || !el.matches(TEXT_ENTRY)) document.documentElement.removeAttribute('data-typing');
    }, 0);
}

function onChange() {
    const layout = currentLayout();
    apply(layout);
    syncViewport();
    if (!layout) document.documentElement.removeAttribute('data-typing');
    listeners.forEach((fn) => fn(layout));
}

// data-ios: for the few things iOS simply doesn't do - e.g. it ignores any
// programmatic volume on media (hardware buttons only), so a volume slider
// there is a control that does nothing. iPadOS reports itself as a Mac, the
// touch-point check catches that. Never set on a desktop.
function isIOS() {
    const ua = navigator.userAgent || '';
    return /iP(hone|ad|od)/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Called once from index.js, BEFORE the first render, so the very first
// paint already has the right layout (no desktop-then-mobile flash).
export function initLayoutMode() {
    if (isIOS()) document.documentElement.setAttribute('data-ios', '');
    apply(currentLayout());
    syncViewport();
    queries.forEach((mq) => {
        // Safari < 14 only has the deprecated addListener.
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange);
    });
    // Rotation - no media query above changes when a phone turns (width can
    // stay under 768px either way), so this is what flips portrait/landscape.
    if (window.screen && window.screen.orientation && window.screen.orientation.addEventListener) {
        window.screen.orientation.addEventListener('change', onChange);
    } else {
        window.addEventListener('orientationchange', onChange);
    }
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', queueSyncViewport);
        window.visualViewport.addEventListener('scroll', queueSyncViewport);
    }
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
}

export function subscribeLayout(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
